import { gql } from '@apollo/client';

// ─── Fragments ──────────────────────────────────────────────────────────────

export const PROFILE_FIELDS = gql`
  fragment ProfileFields on TalentProfile {
    id
    fullName
    userEmail
    position
    seniority
    discipline
    roleId
    role {
      id
      name
      nameEn
    }
    employmentStatus
    country
    managerEmail
    formTokenExpiresAt
    skillsReviewedAt
    skillsReviewedByEmail
    skillsReviewNote
    skillsOnboardedAt
    skillsOnboardingStep
    roleMismatchAt
    englishLevel
    yearsOfExperience
    comments
    aboutMe
    githubUser
    isActive
    updatedAt
  }
`;

export const PROFILE_DETAIL_FIELDS = gql`
  ${PROFILE_FIELDS}
  fragment ProfileDetailFields on TalentProfileDetail {
    canEdit
    canReview
    managerName
    roles {
      id
      name
      nameEn
      description
      descriptionEn
      isActive
    }
    expectedSkillIds
    bonusSkillIds
    unansweredSkillIds
    profile {
      ...ProfileFields
    }
    skills {
      id
      skillId
      level
      yearsOfExperience
      skill {
        id
        name
        category
        categoryGroup
        isCore
        isBonus
      }
    }
    certifications {
      id
      name
      issuer
      issueDate
      expirationDate
      credentialId
      skillId
      skill {
        id
        name
      }
    }
    languages {
      id
      language
      level
    }
    projects {
      id
      projectId
      role
      isCurrent
      startDate
      endDate
      project {
        id
        name
        client
      }
    }
  }
`;

// ─── Queries ────────────────────────────────────────────────────────────────

/**
 * Entry point for the whole module: decides whether the single /skills route
 * renders the admin directory or the person's own profile.
 */
export const GET_TALENT_ACCESS = gql`
  query TalentAccess {
    talentAccess {
      hasProfile
      isAdmin
      moduleEnabled
      canUse
      needsSkillOnboarding
      skillsOnboardingStep
      profileId
      isManager
      reportCount
      formOnly
      unansweredCount
      deskbookingEnabled
    }
  }
`;

export const GET_MY_TALENT_PROFILE = gql`
  ${PROFILE_DETAIL_FIELDS}
  query MyTalentProfile {
    myTalentProfile {
      ...ProfileDetailFields
    }
  }
`;

export const GET_TALENT_PROFILE = gql`
  ${PROFILE_DETAIL_FIELDS}
  query TalentProfile($profileId: Int!) {
    talentProfile(profileId: $profileId) {
      ...ProfileDetailFields
    }
  }
`;

export const GET_TALENT_DIRECTORY = gql`
  ${PROFILE_FIELDS}
  query TalentDirectory($filter: TalentSearchInput) {
    talentDirectory(filter: $filter) {
      roles {
        id
        name
        nameEn
      }
      skillCount
      certificationCount
      matchedLevel
      currentProjects
      profile {
        ...ProfileFields
      }
    }
  }
`;

export const GET_SKILLS = gql`
  query Skills($onlyInUse: Boolean) {
    skills(onlyInUse: $onlyInUse) {
      id
      name
      category
      categoryGroup
      isCore
      isBonus
    }
  }
`;

/**
 * The job roles. Asked for by the role picker, the filters and the role
 * editor; `nameEn` so the client can localise the name itself.
 */
export const GET_TALENT_ROLES = gql`
  query TalentRoles($includeInactive: Boolean) {
    talentRoles(includeInactive: $includeInactive) {
      id
      name
      nameEn
      description
      descriptionEn
      isActive
      sortOrder
      skillCount
      peopleCount
    }
  }
`;

/** Which skills make up one role's grid. Admin only; drives the role editor. */
export const GET_TALENT_ROLE_SKILL_IDS = gql`
  query TalentRoleSkillIds($roleId: Int!) {
    talentRoleSkillIds(roleId: $roleId)
  }
`;

export const GET_SKILL_CATEGORIES = gql`
  query SkillCategories {
    skillCategories
  }
`;

/** Office-wide state of the skills form. Drives the rollout banner. */
export const GET_SKILL_ONBOARDING_PROGRESS = gql`
  query SkillOnboardingProgress {
    skillOnboardingProgress {
      total
      done
      pending
      notStarted
      stale
      staleAfterMonths
      roleMismatch
    }
  }
`;

export const GET_SKILL_COVERAGE = gql`
  query SkillCoverage($roleId: Int, $bonusOnly: Boolean) {
    skillCoverage(roleId: $roleId, bonusOnly: $bonusOnly) {
      skillId
      skillName
      category
      basic
      intermediate
      advanced
      total
      expected
      missing
    }
  }
`;

export const GET_EXPIRING_CERTIFICATIONS = gql`
  query ExpiringCertifications($withinDays: Int) {
    expiringCertifications(withinDays: $withinDays) {
      personName
      daysUntilExpiration
      certification {
        id
        name
        issuer
        expirationDate
      }
    }
  }
`;

/** Open to anybody with module access; carries no skill levels. */
export const GET_TEAM = gql`
  query Team($filter: TeamFilterInput) {
    team(filter: $filter) {
      profileId
      fullName
      position
      userEmail
      discipline
      role
      roleEn
      seniority
      country
      aboutMe
      githubUser
      skills
      currentProjects
      inOfficeToday
      deskToday
      matches {
        skillId
        name
        level
      }
    }
  }
`;

/** A manager's people and where each is with the form. */
export const GET_TEAM_PROGRESS = gql`
  ${PROFILE_FIELDS}
  query TeamProgress($managerEmail: String) {
    teamProgress(managerEmail: $managerEmail) {
      ratedCount
      expectedCount
      advancedCount
      lastAnsweredAt
      changedSinceReview
      roles {
        id
        name
        nameEn
      }
      profile {
        ...ProfileFields
      }
    }
  }
`;

/** Everybody somebody reports to. Admin only. */
export const GET_TALENT_MANAGERS = gql`
  query TalentManagers {
    talentManagers {
      email
      name
      reportCount
    }
  }
`;

/** The grid these roles would be asked. Admin preview of the form. */
export const GET_SKILL_FORM_PREVIEW = gql`
  query SkillFormPreview($roleIds: [Int!]!) {
    skillFormPreview(roleIds: $roleIds) {
      expectedSkillIds
      bonusSkillIds
    }
  }
`;

/** The Skill Matrix at a glance: everybody for admins, their people for a manager. */
export const GET_SKILL_DASHBOARD = gql`
  fragment DashboardGroupFields on DashboardGroup {
    key
    label
    labelEn
    total
    done
    inProgress
    notStarted
    reviewed
  }
  fragment DashboardSkillFields on DashboardSkill {
    skillId
    name
    category
    basic
    intermediate
    advanced
    asked
  }
  query SkillDashboard($filter: DashboardFilterInput) {
    skillDashboard(filter: $filter) {
      progress {
        total
        done
        inProgress
        notStarted
      }
      stale
      reviewed
      withManager
      freelancers
      roleMismatch
      avgRated
      byRole {
        ...DashboardGroupFields
      }
      byCountry {
        ...DashboardGroupFields
      }
      byManager {
        ...DashboardGroupFields
      }
      topSkills {
        ...DashboardSkillFields
      }
      gaps {
        ...DashboardSkillFields
      }
      bonus {
        ...DashboardSkillFields
      }
      weekly {
        week
        completed
      }
      scopeManagerEmail
    }
  }
`;

/** What still stands between the module and a launch. Admin only. */
export const GET_SKILL_LAUNCH_STATUS = gql`
  query SkillLaunchStatus {
    skillLaunchStatus {
      moduleEnabled
      alertsEnabled
      viewers
      people
      withAccess
      withManager
      noRole
      freelancersNoEmail
      emptyRoles
      skillsInNoGrid
    }
  }
`;

/** Every catalogue skill with its usage. Admin only. */
export const GET_SKILL_CATALOG = gql`
  query SkillCatalog {
    skillCatalog {
      people
      roles
      certifications
      skill {
        id
        name
        category
        isActive
        isBonus
        isCore
      }
    }
  }
`;
